import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import api, { RBAC_BASE } from "../../../../../api/rbacApi";
import { employeeApi } from "../../../../../api/employeeApi";
import { useNavigate } from "react-router-dom";
import {
  validateEmail,
  validatePhone,
  validateText,
  validateImageFile,
  sanitizeString,
  validateDate,
} from "../../../../../utils/inputValidator";

const AddEmployees = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [roleOptions, setRoleOptions] = useState([]);
  const [roleLoading, setRoleLoading] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    employeeName: "",
    employeeCompany: "",
    employeeProfile: null,
    gender: "",
    joiningDate: "",
    dob: "",
    password: "",
    email: "",
    phone: "",
    department: "",
    designation: "",
    designationId: "",
    address: "",
  });

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setRoleLoading(true);
        const res = await api.get(`${RBAC_BASE}/roles`);
        const options = Array.isArray(res.data)
          ? res.data.filter((role) => role.bitStatus !== 0)
          : [];
        setRoleOptions(options);
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load roles.");
      } finally {
        setRoleLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const showToast = (icon, title) => {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon,
      title,
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
    });
  };

  const validateFormFields = () => {
    const errors = {};

    // Validate employee name 
    const nameValidation = validateText(formData.employeeName, {
      minLength: 2,
      maxLength: 100,
      rejectHTML: true,
    });
    if (!nameValidation.isValid) errors.employeeName = nameValidation.error;

    // Validate company name
    const companyValidation = validateText(formData.employeeCompany, {
      minLength: 2,
      maxLength: 100,
      rejectHTML: true,
    });
    if (!companyValidation.isValid)
      errors.employeeCompany = companyValidation.error;

    // Validate email
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) errors.email = emailValidation.error;

    // Validate phone
    if (formData.phone) {
      const phoneValidation = validatePhone(formData.phone);
      if (!phoneValidation.isValid) errors.phone = phoneValidation.error;
    }

    // Validate department
    if (formData.department) {
      const deptValidation = validateText(formData.department, {
        minLength: 2,
        maxLength: 50,
        rejectHTML: true,
      });
      if (!deptValidation.isValid) errors.department = deptValidation.error;
    }

    // Validate designation
    if (formData.designation) {
      const desigValidation = validateText(formData.designation, {
        minLength: 2,
        maxLength: 50,
        rejectHTML: true,
      });
      if (!desigValidation.isValid) errors.designation = desigValidation.error;
    }

    // Validate password
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (formData.password) {
      if (formData.password.length < 8) {
        errors.password = "Password must be at least 8 characters";
      } else if (!passwordRegex.test(formData.password)) {
        errors.password =
          "Password must contain uppercase, lowercase, number, and special character";
      }
    }

    // Validate address
    if (formData.address) {
      const addressValidation = validateText(formData.address, {
        minLength: 5,
        maxLength: 500,
        rejectHTML: true,
      });
      if (!addressValidation.isValid) errors.address = addressValidation.error;
    }

    // Validate gender
    if (!formData.gender) errors.gender = "Please select a gender";

    // Validate joining date
    if (formData.joiningDate) {
      const dateValidation = validateDate(formData.joiningDate);
      if (!dateValidation.isValid) errors.joiningDate = dateValidation.error;
    }

    // Validate profile image if provided
    if (formData.employeeProfile) {
      const fileValidation = validateImageFile(formData.employeeProfile, {
        maxSizeInMB: 5,
      });
      if (!fileValidation.isValid)
        errors.employeeProfile = fileValidation.error;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateForm = () => {
    if (!formData.employeeName.trim()) {
      return "Employee Name is required.";
    }

    if (!String(formData.phone || "").trim()) {
      return "Mobile Number is required.";
    }

    if (!String(formData.password || "").trim()) {
      return "Password is required.";
    }

    if (!String(formData.designationId || "").trim()) {
      return "Role is required.";
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!validateFormFields()) {
      setError("Please fix the validation errors below.");
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      showToast("error", validationError);
      return;
    }

    // Whitelist allowed fields & sanitize string inputs to prevent XSS / script injection
    const ALLOWED_FIELDS = [
      "employeeName",
      "employeeCompany",
      "employeeProfile",
      "gender",
      "joiningDate",
      "dob",
      "password",
      "email",
      "phone",
      "department",
      "designation",
      "designationId",
      "address",
    ];

    const sanitizedPayload = {};

    ALLOWED_FIELDS.forEach((key) => {
      const value = formData[key];

      if (value === null || value === undefined) {
        return;
      }

      if (key === "employeeProfile") {
        if (value instanceof File) {
          sanitizedPayload[key] = value;
        }
      } else if (typeof value === "string") {
        const trimmed = value.trim();
        if (key === "password") {
          sanitizedPayload[key] = trimmed;
        } else {
          sanitizedPayload[key] = sanitizeString(trimmed);
        }
      } else {
        sanitizedPayload[key] = value;
      }
    });

    setLoading(true);

    try {
      await employeeApi.addEmployee(sanitizedPayload);
      setFormData({
        employeeName: "",
        employeeCompany: "",
        employeeProfile: null,
        gender: "",
        joiningDate: "",
        dob: "",
        password: "",
        email: "",
        phone: "",
        department: "",
        designation: "",
        designationId: "",
        address: "",
      });
      setFieldErrors({});
      showToast("success", "Employee added successfully");
      navigate("/employees-all");
    } catch (err) {
      const message =
        typeof err === "string"
          ? err
          : err?.response?.data?.message ||
            err?.message ||
            "Failed to add employee. Please try again.";
      setError(message);
      showToast("error", message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "file" ? files[0] : value,
    }));
    // Clear field error on edit
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleDesignationChange = (e) => {
    const selectedId = e.target.value;
    setFormData((prevData) => ({
      ...prevData,
      designationId: selectedId,
    }));
    if (fieldErrors.designationId) {
      setFieldErrors((prev) => ({ ...prev, designationId: null }));
    }
  };

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
        <div>
          <h3 className="fw-bold mb-1">Register Employees</h3>
          <small className="text-muted">
            Add a new employee and configure their organizational details and
            HRMS account.
          </small>
        </div>
      </div>
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4 p-xl-5">
          <form className="row g-3 g-xl-4" onSubmit={handleSubmit}>
            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="text"
                  name="employeeName"
                  value={formData.employeeName}
                  onChange={handleInputChange}
                  className={`form-control ${
                    fieldErrors.employeeName ? "is-invalid" : ""
                  }`}
                  id="employeeName"
                  placeholder="Employee Name"
                />
                <label htmlFor="employeeName">Employee Name</label>
                {fieldErrors.employeeName && (
                  <div className="invalid-feedback d-block">
                    {fieldErrors.employeeName}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className={`form-select ${
                    fieldErrors.gender ? "is-invalid" : ""
                  }`}
                  id="gender"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                <label htmlFor="gender">Gender</label>
                {fieldErrors.gender && (
                  <div className="invalid-feedback d-block">
                    {fieldErrors.gender}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleInputChange}
                  className={`form-control ${
                    fieldErrors.dob ? "is-invalid" : ""
                  }`}
                  id="dob"
                  placeholder="Date of Birth"
                />
                <label htmlFor="dob">Date of Birth</label>
                {fieldErrors.dob && (
                  <div className="invalid-feedback d-block">
                    {fieldErrors.dob}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label text-muted">Employee Profile</label>
              <input
                className={`form-control ${
                  fieldErrors.employeeProfile ? "is-invalid" : ""
                }`}
                type="file"
                name="employeeProfile"
                onChange={handleInputChange}
              />
              {fieldErrors.employeeProfile && (
                <div className="invalid-feedback d-block">
                  {fieldErrors.employeeProfile}
                </div>
              )}
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="text"
                  name="employeeCompany"
                  value={formData.employeeCompany}
                  onChange={handleInputChange}
                  className={`form-control ${
                    fieldErrors.employeeCompany ? "is-invalid" : ""
                  }`}
                  id="employeeCompany"
                  placeholder="Company Name"
                />
                <label htmlFor="employeeCompany">Employee Company</label>
                {fieldErrors.employeeCompany && (
                  <div className="invalid-feedback d-block">
                    {fieldErrors.employeeCompany}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  className={`form-control ${
                    fieldErrors.department ? "is-invalid" : ""
                  }`}
                  id="department"
                  placeholder="Department"
                />
                <label htmlFor="department">Department / Unit</label>
                {fieldErrors.department && (
                  <div className="invalid-feedback d-block">
                    {fieldErrors.department}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="text"
                  name="designation"
                  value={formData.designation}
                  onChange={handleInputChange}
                  className={`form-control ${
                    fieldErrors.designation ? "is-invalid" : ""
                  }`}
                  id="designation"
                  placeholder="Designation"
                />
                <label htmlFor="designation">Designation</label>
                {fieldErrors.designation && (
                  <div className="invalid-feedback d-block">
                    {fieldErrors.designation}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <select
                  name="designationId"
                  value={formData.designationId}
                  onChange={handleDesignationChange}
                  className={`form-select ${
                    fieldErrors.designationId ? "is-invalid" : ""
                  }`}
                  id="role"
                  disabled={roleLoading}
                >
                  <option value="">
                    {roleLoading ? "Loading roles..." : "Select Role"}
                  </option>
                  {roleOptions.map((role) => (
                    <option key={role.intDesigId} value={role.intDesigId}>
                      {role.nvchDesigName}
                    </option>
                  ))}
                </select>
                <label htmlFor="role">Role</label>
                {fieldErrors.designationId && (
                  <div className="invalid-feedback d-block">
                    {fieldErrors.designationId}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="date"
                  name="joiningDate"
                  value={formData.joiningDate}
                  onChange={handleInputChange}
                  className={`form-control ${
                    fieldErrors.joiningDate ? "is-invalid" : ""
                  }`}
                  id="joiningDate"
                  placeholder="Joining Date"
                />
                <label htmlFor="joiningDate">Joining Date</label>
                {fieldErrors.joiningDate && (
                  <div className="invalid-feedback d-block">
                    {fieldErrors.joiningDate}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`form-control ${
                    fieldErrors.phone ? "is-invalid" : ""
                  }`}
                  id="phone"
                  placeholder="Phone Number"
                />
                <label htmlFor="phone">Phone Number</label>
                {fieldErrors.phone && (
                  <div className="invalid-feedback d-block">
                    {fieldErrors.phone}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`form-control ${
                    fieldErrors.email ? "is-invalid" : ""
                  }`}
                  id="email"
                  placeholder="Email ID"
                />
                <label htmlFor="email">Email ID</label>
                {fieldErrors.email && (
                  <div className="invalid-feedback d-block">
                    {fieldErrors.email}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-floating">
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`form-control ${
                    fieldErrors.password ? "is-invalid" : ""
                  }`}
                  id="password"
                  placeholder="Password"
                />
                <label htmlFor="password">Password</label>
                {fieldErrors.password && (
                  <div className="invalid-feedback d-block">
                    {fieldErrors.password}
                  </div>
                )}
              </div>
            </div>

            <div className="col-12">
              <div className="form-floating">
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={`form-control ${
                    fieldErrors.address ? "is-invalid" : ""
                  }`}
                  id="address"
                  rows="3"
                  placeholder="Employee Address"
                ></textarea>
                <label htmlFor="address">Address</label>
                {fieldErrors.address && (
                  <div className="invalid-feedback d-block">
                    {fieldErrors.address}
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="col-12">
                <div
                  className="alert alert-danger alert-dismissible fade show rounded-3"
                  role="alert"
                >
                  {error}
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="alert"
                    aria-label="Close"
                  ></button>
                </div>
              </div>
            )}

            <div className="col-12 d-flex justify-content-end pt-2">
              <button
                type="submit"
                className="btn btn-primary btn-lg px-4"
                disabled={loading || roleLoading}
              >
                {loading ? "Adding Employee..." : "Register Employee"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddEmployees;
